/**
 * Inline-editable field row — the unit of detail-page editing.
 *
 * Two modes:
 *   - Read-only (`editor` omitted): renders label + value text, no
 *     hover/click affordance.
 *   - Editable: renders label + a click-to-edit button. When the
 *     parent flips `editing` true, the editor JSX replaces the
 *     display. Save semantics live in the editor (onBlur, Enter, etc.)
 *     — this component is pure presentation.
 *
 * Extracted from InitiativeDetail.tsx so the Project Detail page
 * can adopt the same pattern (the design choice landed when we
 * standardised on inline-editing for entity detail pages and
 * demoted modals to creation-only).
 */
import type React from "react";

export interface EditableFieldProps {
  label: string;
  /** Shortcut for read-only fields when you just want a string. */
  value?: string;
  /** Custom display JSX (pills, formatted numbers, etc.). */
  display?: React.ReactNode;
  /** Editor JSX (input, select, picker). Omit to make read-only. */
  editor?: React.ReactNode;
  editing?: boolean;
  onEdit?: () => void;
  /** Width of the label column; defaults to w-28 to match the
   *  existing Initiative Detail layout. */
  labelWidth?: string;
}

export function EditableField({
  label,
  value,
  display,
  editor,
  editing,
  onEdit,
  labelWidth = "w-28",
}: EditableFieldProps) {
  if (!editor) {
    return (
      <div className="flex items-baseline gap-2">
        <span className={`text-ink-muted font-medium ${labelWidth} shrink-0`}>
          {label}
        </span>
        <span className="text-ink">{value ?? "—"}</span>
      </div>
    );
  }

  return (
    <div className="flex items-baseline gap-2">
      <span className={`text-ink-muted font-medium ${labelWidth} shrink-0`}>
        {label}
      </span>
      {editing ? (
        editor
      ) : (
        <button
          onClick={onEdit}
          className="text-left hover:text-brand-navy-700 dark:hover:text-brand-navy-300 transition-colors"
        >
          {display}
        </button>
      )}
    </div>
  );
}
